import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../../infrastructure/api/client";
import type {
  GroupMemberResponseDto,
  TripResponseDto,
  GroupRole,
  MemberType,
} from "@tabipla/shared";
import { ApiError, type GroupResponseDto } from "@tabipla/shared";

// Fetch all groups for the current user
export function useGroups() {
  return useQuery({
    queryKey: ["groups"],
    queryFn: async () => {
      const response = await apiClient.get<GroupResponseDto[]>("/api/groups");
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch groups");
      }
      return response.data ?? [];
    },
  });
}

// Fetch single group
export function useGroup(groupId: string | undefined) {
  return useQuery({
    queryKey: ["groups", groupId],
    queryFn: async () => {
      if (!groupId) throw new Error("Group ID is required");
      const response = await apiClient.get<GroupResponseDto>(`/api/groups/${groupId}`);
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch group");
      }
      return response.data;
    },
    enabled: !!groupId,
  });
}

// Fetch group members
export function useGroupMembers(groupId: string | undefined) {
  return useQuery({
    queryKey: ["groups", groupId, "members"],
    queryFn: async () => {
      if (!groupId) throw new Error("Group ID is required");
      const response = await apiClient.get<GroupMemberResponseDto[]>(
        `/api/groups/${groupId}/members`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch members");
      }
      return response.data ?? [];
    },
    enabled: !!groupId,
  });
}

// Fetch trips in a group
export function useGroupTrips(groupId: string | undefined) {
  return useQuery({
    queryKey: ["groups", groupId, "trips"],
    queryFn: async () => {
      if (!groupId) throw new Error("Group ID is required");
      const response = await apiClient.get<TripResponseDto[]>(`/api/groups/${groupId}/trips`);
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch group trips");
      }
      return response.data ?? [];
    },
    enabled: !!groupId,
  });
}

// Create group
interface CreateGroupInput {
  name: string;
  description?: string | null;
}

export function useCreateGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateGroupInput) => {
      const response = await apiClient.post<GroupResponseDto>("/api/groups", input);
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to create group");
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
    },
  });
}

// Update group
interface UpdateGroupInput {
  name?: string;
  description?: string | null;
  // Icon fields (emoji OR image, mutually exclusive)
  iconEmoji?: string | null;
  iconColor?: string | null;
  iconImageUrl?: string | null;
  iconImageKey?: string | null;
  // Cover image
  coverImageUrl?: string | null;
  coverImageKey?: string | null;
  // Background (image OR color/gradient)
  backgroundImageUrl?: string | null;
  backgroundImageKey?: string | null;
  backgroundColor?: string | null;
}

export function useUpdateGroup(groupId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateGroupInput) => {
      const response = await apiClient.put<GroupResponseDto>(`/api/groups/${groupId}`, input);
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to update group");
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
      queryClient.invalidateQueries({ queryKey: ["groups", groupId] });
    },
  });
}

// Delete group
export function useDeleteGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (groupId: string) => {
      const response = await apiClient.delete<null>(`/api/groups/${groupId}`);
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to delete group");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
      // Also invalidate trips as they may reference the deleted group
      queryClient.invalidateQueries({ queryKey: ["trips"] });
    },
  });
}

// Invite member
interface InviteMemberInput {
  email: string;
  role: Exclude<GroupRole, "owner">;
}

export function useInviteMember(groupId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: InviteMemberInput) => {
      const response = await apiClient.post<GroupMemberResponseDto>(
        `/api/groups/${groupId}/members`,
        input
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to invite member");
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groups", groupId, "members"] });
      queryClient.invalidateQueries({ queryKey: ["groups", groupId] });
      queryClient.invalidateQueries({ queryKey: ["groups"] });
    },
  });
}

// Update member role
export function useUpdateMemberRole(groupId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      memberId,
      memberType,
      role,
    }: {
      memberId: string;
      memberType: MemberType;
      role: Exclude<GroupRole, "owner">;
    }) => {
      const response = await apiClient.put<GroupMemberResponseDto>(
        `/api/groups/${groupId}/members/${memberId}?memberType=${memberType}`,
        { role }
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to update member role");
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groups", groupId, "members"] });
    },
  });
}

// Remove member
export function useRemoveMember(groupId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ memberId, memberType }: { memberId: string; memberType: MemberType }) => {
      const response = await apiClient.delete<null>(
        `/api/groups/${groupId}/members/${memberId}?memberType=${memberType}`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to remove member");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groups", groupId, "members"] });
      queryClient.invalidateQueries({ queryKey: ["groups", groupId] });
      queryClient.invalidateQueries({ queryKey: ["groups"] });
    },
  });
}

// Assign trip to group
export function useAssignTripToGroup(tripId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (groupId: string | null) => {
      const response = await apiClient.put<TripResponseDto>(`/api/trips/${tripId}/group`, {
        groupId,
      });
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to assign trip to group");
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trips"] });
      queryClient.invalidateQueries({ queryKey: ["trips", tripId] });
      queryClient.invalidateQueries({ queryKey: ["groups"] });
    },
  });
}

// Response type for create-group endpoint
interface CreateGroupForTripResponse {
  trip: TripResponseDto;
  groupId: string;
}

// Create group for trip (creates group + assigns it to the trip)
export function useCreateGroupForTrip(tripId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.post<CreateGroupForTripResponse>(
        `/api/trips/${tripId}/create-group`
      );
      if (!response.success || !response.data) {
        throw ApiError.fromResponse(response.error, "Failed to create group for trip");
      }
      return response.data;
    },
    onSuccess: (data) => {
      // Update trip cache directly with the new groupId
      queryClient.setQueryData(["trips", tripId], (oldData: TripResponseDto | undefined) => {
        if (!oldData) return oldData;
        return { ...oldData, groupId: data.groupId };
      });
      queryClient.invalidateQueries({ queryKey: ["trips"] });
      queryClient.invalidateQueries({ queryKey: ["groups"] });
    },
  });
}
